<template>
  <BaseScreen v-bind="screen">
    <BaseMenu
      :items="menuItems"
    />
    <BaseButton
      v-if="personalButton"
      v-bind="personalButton"
      slot="actions"
      @click="goToPersonalScreen"
    />
    <BaseOverlay
      v-if="isWarningOverlayVisible"
      v-bind="warningOverlay"
      class="screen__overlay"
      slot="root"
    />
  </BaseScreen>
</template>

<script>
import { mapGetters } from 'vuex';
import CONSTANTS from '@/helpers/constants';

import screenMixin from '@/mixins/screen';

import BaseMenu from '@/components/BaseMenu.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseOverlay from '@/components/BaseOverlay.vue';

export default {
  name: 'HomeScreen',
  mixins: [screenMixin],
  components: {
    BaseMenu,
    BaseButton,
    BaseOverlay,
  },
  data() {
    return {
      isInteractionStarted: false,
      isWarningOverlayVisible: false,
    };
  },
  computed: {
    ...mapGetters('personal', [
      'isAuthenticated',
    ]),
    screenCase() {
      if (!this.isInteractionStarted) {
        return CONSTANTS.STEP_START;
      }
      if (this.isWarningOverlayVisible) {
        return CONSTANTS.STEP_COVID;
      }
      return CONSTANTS.STEP_HOME;
    },
    personalButton() {
      return this.isAuthenticated ? {
        text: this.$t('titles.personal'),
        theme: 'secondary',
      } : null;
    },
    menuItems() {
      return [
        this.isAuthenticated ? null : {
          icon: {
            name: 'home',
            theme: 'primary',
            size: 's',
          },
          label: {
            text: this.$t('actions.signin'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToSignInScreen,
        },
        {
          icon: {
            name: 'qr',
            theme: 'secondary',
            size: 'l',
          },
          label: {
            text: this.$t('actions.payByQr'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToQrScreen,
        },
        this.isAuthenticated ? null : {
          icon: {
            name: 'signup',
            theme: 'primary',
            size: 's',
          },
          label: {
            text: this.$t('actions.signup'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToSignUpScreen,
        },
        {
          icon: {
            name: 'meter',
            theme: 'secondary',
            size: 'l',
          },
          label: {
            text: this.$t('actions.transmit'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToAccountScreen,
        },
        {
          icon: {
            name: 'feedback',
            theme: 'plain',
            size: 's',
          },
          label: {
            text: this.$t('actions.addFeedback'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          isInline: true,
          action: this.goToFeedbackScreen,
        },
        {
          icon: {
            name: 'photo',
            theme: 'plain',
            size: 's',
          },
          label: {
            text: this.$t('actions.toPhoto'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          isInline: true,
          action: this.goToPhotoScreen,
        },
      ].filter(Boolean);
    },
    warningOverlay() {
      return {
        description: {
          text: this.$t('texts.healthWarning'),
          size: 'xxl',
        },
        actions: [
          {
            text: this.$t('actions.skipTemperatureMeasure'),
            action: this.hideWarningOverlay,
            theme: 'light',
            view: 'outlined',
          },
          {
            text: this.$t('actions.temperatureMeasure'),
            action: this.goToFindFaceScreen,
            theme: 'light',
          },
        ],
      };
    },
  },
  beforeRouteEnter(to, from, next) {
    const isStopped = to.params[CONSTANTS.INTERACTION_STOPPED];
    const hasPrevName = Boolean(from?.name); // Во время первой загрузки from.name = null
    const isStarted = typeof isStopped === 'boolean' ? !isStopped : hasPrevName;
    return next((vm) => (vm.isInteractionStarted = isStarted)); // eslint-disable-line
  },
  created() {
    this.initActions([
      [CONSTANTS.ACTION_PERSONAL, this.goToPersonalScreen],
      [CONSTANTS.ACTION_SIGN_UP, this.goToSignUpScreen],
      [CONSTANTS.ACTION_SIGN_IN, this.goToSignInScreen],
      [CONSTANTS.ACTION_SEND_METER, this.goToAccountScreen],
      [CONSTANTS.ACTION_ADD_FEEDBACK, this.goToFeedbackScreen],
      [CONSTANTS.ACTION_TAKE_PHOTO, this.goToPhotoScreen],
      [CONSTANTS.ACTION_SCAN_QR, this.goToQrScreen],
      [CONSTANTS.ACTION_DISAGREE, this.hideWarningOverlay],
      [CONSTANTS.ACTION_AGREE, this.goToFindFaceScreen],
    ]);
  },
  methods: {
    onInteractionStarted() {
      this.isInteractionStarted = true;
      this.showWarningOverlay();
    },
    onInteractionStopped() {
      this.isInteractionStarted = false;
      this.hideWarningOverlay();
    },
    showWarningOverlay() {
      this.isWarningOverlayVisible = true;
    },
    hideWarningOverlay() {
      this.isWarningOverlayVisible = false;
    },
    goToPersonalScreen() {
      this.$router.push(this.$router.options.paths.personal);
    },
    goToSignUpScreen() {
      this.$router.push(this.$router.options.paths.signUp);
    },
    goToSignInScreen() {
      this.$router.push(this.$router.options.paths.signIn);
    },
    goToAccountScreen() {
      this.$router.push(this.$router.options.paths.guestAccount);
    },
    goToFeedbackScreen() {
      this.$router.push(this.$router.options.paths.feedback);
    },
    goToPhotoScreen() {
      this.$router.push(this.$router.options.paths.photoTake);
    },
    goToQrScreen() {
      this.$router.push(this.$router.options.paths.qr);
    },
    goToFindFaceScreen() {
      this.$router.push(this.$router.options.paths.findFace);
    },
  },
};
</script>
